type ApiErrorBody = {
  errors?: Record<string, string | undefined>;
  message?: unknown;
};

type BoxedApiError = {
  response?: {
    status?: unknown;
    data?: ApiErrorBody & { statusCode?: unknown };
  };
  error?: unknown;
  status?: unknown;
  statusCode?: unknown;
};

/** `error.response` on a null query error throws; React Query uses `error: null`. */
function boxedApiError(err: unknown): BoxedApiError | undefined {
  if (err === null || typeof err !== 'object') return undefined;
  return err as BoxedApiError;
}

/**
 * First validation error code from a 422-style API response body.
 */
export function getApiValidationErrorCode(
  err: unknown,
  field?: string,
): string | undefined {
  const errors = boxedApiError(err)?.response?.data?.errors;
  if (!errors) {
    return undefined;
  }
  if (field) {
    return errors[field];
  }
  const firstKey = Object.keys(errors)[0];
  return firstKey ? errors[firstKey] : undefined;
}

function readBusinessErrorCode(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const direct = (data as { error?: unknown }).error;
  if (typeof direct === 'string' && direct.length > 0) {
    // Nest string exceptions use HTTP phrase here ("Conflict"); domain codes don't.
    if (!direct.includes(' ') && direct !== 'Conflict') return direct;
  }
  const message = (data as { message?: unknown }).message;
  if (typeof message === 'object' && message !== null) {
    const nested = (message as { error?: unknown }).error;
    if (typeof nested === 'string' && nested.length > 0) return nested;
  }
  return undefined;
}

/**
 * Domain error code from `{ error: 'eventNameExists' }`-style API bodies.
 * Nest sometimes wraps that object in `message`; hey-api may put the body on `error`.
 */
export function getApiBusinessErrorCode(err: unknown): string | undefined {
  const boxed = boxedApiError(err);
  if (!boxed) return undefined;
  return (
    readBusinessErrorCode(boxed.response?.data) ??
    readBusinessErrorCode(boxed.error)
  );
}

/**
 * Best-effort message from API client / axios-style errors.
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  const data = boxedApiError(err)?.response?.data;
  if (typeof data === 'object' && data !== null && 'message' in data) {
    return String((data as { message: unknown }).message);
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

export function getApiErrorStatus(err: unknown): number | undefined {
  const boxed = boxedApiError(err);
  if (!boxed) return undefined;
  if (typeof boxed.response?.status === 'number') {
    return boxed.response.status;
  }
  if (typeof boxed.status === 'number') {
    return boxed.status;
  }
  if (typeof boxed.statusCode === 'number') {
    return boxed.statusCode;
  }
  const nested = boxed.response?.data?.statusCode;
  if (typeof nested === 'number') {
    return nested;
  }
  return undefined;
}

/** 401/403 are permission outcomes, not retryable load failures. */
export function isAuthzApiError(err: unknown): boolean {
  const status = getApiErrorStatus(err);
  return status === 401 || status === 403;
}
